import { useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { DEFAULT_REPORT_FILTERS } from "@/lib/reportDefaults";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { ReportExport } from "@/components/reports/ReportExport";
import { formatCurrency, cn } from "@/lib/utils";
import {
  PiggyBank,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  Wallet,
  HandCoins,
  FileCheck,
  FileX,
  TrendingUp,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface SavingsData {
  summary: {
    declaredSavings: number;
    approvedSavings: number;
    payrollDeducted: number;
    manualSavings: number;
    pendingApproval: number;
    rejected: number;
    missingDeclarations: number;
  };
  monthlyComparison: Array<{
    month: string;
    declared: number;
    approved: number;
  }>;
  memberContributions: Array<{
    memberId: string;
    memberName: string;
    totalDeclared: number;
    approved: number;
    pending: number;
    rejected: number;
    status: string;
  }>;
}

function SummaryCard({
  title,
  value,
  icon,
  accentColor,
  loading = false,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accentColor: string;
  loading?: boolean;
}) {
  const bgMap: Record<string, string> = {
    green: "bg-[#009A44]/10",
    red: "bg-red-50",
    blue: "bg-blue-50",
    amber: "bg-amber-50",
    purple: "bg-purple-50",
    gray: "bg-gray-100",
  };
  const colorMap: Record<string, string> = {
    green: "text-[#009A44]",
    red: "text-red-600",
    blue: "text-blue-600",
    amber: "text-amber-600",
    purple: "text-purple-600",
    gray: "text-gray-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">{title}</span>
          <div
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg",
              bgMap[accentColor] || "bg-gray-50"
            )}
          >
            <div className={cn("h-4 w-4", colorMap[accentColor] || "text-gray-600")}>
              {icon}
            </div>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <p className={cn("text-xl font-bold", colorMap[accentColor] || "text-gray-900")}>
            {typeof value === "number" ? formatCurrency(value) : value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const statusStyles: Record<string, string> = {
  compliant: "bg-[#009A44]/10 text-[#009A44]",
  partial: "bg-amber-50 text-amber-600",
  non_compliant: "bg-red-50 text-red-600",
  pending: "bg-blue-50 text-blue-600",
};

export default function SavingsReport() {
  const [filters, setFilters] = useState<Record<string, any>>(DEFAULT_REPORT_FILTERS);

  const {
    data: rawData,
    status,
    error,
    refetch,
  } = useQuery(
    ["reports-savings", filters],
    async () => {
      const params: Record<string, string> = {};
      if (filters.dateStart) params.dateStart = filters.dateStart;
      if (filters.dateEnd) params.dateEnd = filters.dateEnd;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;

      const { data } = await api.get("/reports/savings", { params });
      return data?.data as SavingsData;
    },
    {
      staleTime: 30000,
      cacheTime: 60000,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const loading = status === "loading";
  const summary = rawData?.summary || {
    declaredSavings: 0,
    approvedSavings: 0,
    payrollDeducted: 0,
    manualSavings: 0,
    pendingApproval: 0,
    rejected: 0,
    missingDeclarations: 0,
  };
  const monthlyData = rawData?.monthlyComparison || [];
  const memberContributions = rawData?.memberContributions || [];

  const exportColumns = [
    { key: "memberName", label: "Member" },
    { key: "totalDeclared", label: "Total Declared" },
    { key: "approved", label: "Approved" },
    { key: "pending", label: "Pending" },
    { key: "rejected", label: "Rejected" },
    { key: "status", label: "Status" },
  ];

  if (status === "error") {
    return (
      <div className="min-h-screen w-full max-w-full overflow-x-hidden">
        <div className="space-y-5 min-w-0">
          <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    Savings Report
                  </h1>
                  <p className="mt-1.5 text-sm sm:text-base text-white/80">
                    Savings declarations and contribution tracking
                  </p>
                </div>
              </div>
            </div>
          </Card>
          <div className="flex min-h-[50vh] items-center justify-center p-6">
            <div className="text-center">
              <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-red-400" />
              <h2 className="mb-2 text-lg font-semibold text-gray-900">
                Failed to load savings report
              </h2>
              <p className="mb-4 text-sm text-gray-500">
                {(error as any)?.response?.data?.message ||
                  (error as any)?.message ||
                  "Could not connect to the server."}
              </p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden">
      <div className="space-y-5 min-w-0">
        {/* Header */}
        <Card className="border-none shadow-sm bg-gradient-to-r from-[#009A44] to-[#007a35] overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
                    <PiggyBank className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                      Savings Report
                    </h1>
                    <p className="mt-0.5 text-sm sm:text-base text-white/80">
                      Savings declarations and contribution tracking
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/20 shrink-0"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          show={["dateStart", "dateEnd", "month", "year"]}
        />

        {/* Summary Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            title="Declared Savings"
            value={summary.declaredSavings}
            icon={<FileCheck className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Approved Savings"
            value={summary.approvedSavings}
            icon={<CheckCircle2 className="h-5 w-5" />}
            accentColor="green"
            loading={loading}
          />
          <SummaryCard
            title="Payroll Deducted"
            value={summary.payrollDeducted}
            icon={<Wallet className="h-5 w-5" />}
            accentColor="blue"
            loading={loading}
          />
          <SummaryCard
            title="Manual Savings"
            value={summary.manualSavings}
            icon={<HandCoins className="h-5 w-5" />}
            accentColor="purple"
            loading={loading}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <SummaryCard
            title="Pending Approval"
            value={summary.pendingApproval}
            icon={<Clock className="h-5 w-5" />}
            accentColor="amber"
            loading={loading}
          />
          <SummaryCard
            title="Rejected"
            value={summary.rejected}
            icon={<FileX className="h-5 w-5" />}
            accentColor="red"
            loading={loading}
          />
          <SummaryCard
            title="Missing Declarations"
            value={summary.missingDeclarations}
            icon={<AlertTriangle className="h-5 w-5" />}
            accentColor="gray"
            loading={loading}
          />
        </div>

        {/* Monthly Comparison Chart */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                  <BarChart3 className="h-3.5 w-3.5 text-[#009A44]" />
                </div>
                <h3 className="text-sm font-semibold">Monthly Comparison (Last 6 Months)</h3>
              </div>
            </div>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : monthlyData.length === 0 ? (
              <div className="py-12 text-center">
                <BarChart3 className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No monthly data available</p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                    <Bar dataKey="declared" name="Declared" fill="#009A44" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="approved" name="Approved" fill="#00C853" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Member Contribution Status Table */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#009A44]/10">
                  <TrendingUp className="h-3.5 w-3.5 text-[#009A44]" />
                </div>
                <h3 className="text-sm font-semibold">Member Contribution Status</h3>
              </div>
              <ReportExport
                data={memberContributions}
                filename="savings-report"
                title="Savings Report"
                columns={exportColumns}
              />
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            ) : memberContributions.length === 0 ? (
              <div className="py-12 text-center">
                <PiggyBank className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm text-gray-500">No member contribution data available</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium text-gray-500">Member</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Total Declared</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Approved</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Pending</th>
                      <th className="pb-2 pr-4 font-medium text-gray-500 text-right">Rejected</th>
                      <th className="pb-2 font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberContributions.map((member) => (
                      <tr
                        key={member.memberId}
                        className="border-b last:border-b-0 hover:bg-gray-50/50"
                      >
                        <td className="py-3 pr-4 font-medium text-gray-900">
                          {member.memberName}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-gray-900">
                          {formatCurrency(member.totalDeclared)}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-[#009A44]">
                          {formatCurrency(member.approved)}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-amber-600">
                          {formatCurrency(member.pending)}
                        </td>
                        <td className="py-3 pr-4 text-right font-semibold text-red-600">
                          {formatCurrency(member.rejected)}
                        </td>
                        <td className="py-3">
                          <span
                            className={cn(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                              statusStyles[member.status] || "bg-gray-100 text-gray-600"
                            )}
                          >
                            {member.status.replace("_", " ")}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
